import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import sample from 'lodash/sample';
import { useMediaQuery, useTheme } from '@mui/material';
import { actions } from 'services/store/ai';
import { aiMusicGeneratorAwsApi } from 'services/api/aiMusicGenerator/aiMusicGeneratorApi';
import {
  selectPlaceholderPrompt,
  selectCurrentTextPrompt,
  selectHasUploadedAudio,
} from 'services/store/ai/selectors';
import useFormulaSubmit from '../../useFormulaSubmit';
import GenerateButton from '../GenerateButton';
import PromptTypeToggle from '../PromptTypeToggle';
import { UserLoop } from '../UserLoop';
import {
  StyledButtonContainer,
  StyledCharacterCount,
  StyledContainer,
  StyledFormulaContainer,
  StyledHintTextWrapper,
  StyledInstrumentalLabel,
  StyledTextField,
} from './styled';
import TextToMusicLoopField from './TextToMusicLoopField';

const MAX_LENGTH = 250;

const TextToMusicFormulaPanel = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    data: prompts,
    isLoading,
    error,
  } = aiMusicGeneratorAwsApi.useGetTextToMusicPlaceholderQuery();

  const text = useSelector(selectCurrentTextPrompt);
  const placeholderPrompt = useSelector(selectPlaceholderPrompt);
  const hasUploadedAudio = useSelector(selectHasUploadedAudio);
  const submitFormula = useFormulaSubmit();

  const fallbackPrompt = useMemo(
    () => t('aiMusicGenerator:textToMusic:fallbackPrompt'),
    [t]
  );

  const getRandomPrompt = useCallback(
    (promptArray: string[] | undefined) => {
      if (!promptArray?.length) {
        return fallbackPrompt;
      }

      const randomPrompt = sample(promptArray);

      if (!randomPrompt) {
        return fallbackPrompt;
      }

      return randomPrompt;
    },
    [fallbackPrompt]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (error) {
      dispatch(actions.setPlaceholderPrompt(fallbackPrompt));
      return;
    }

    const randomPrompt = getRandomPrompt(prompts);
    dispatch(actions.setPlaceholderPrompt(randomPrompt));
  }, [prompts, isLoading, error, dispatch, t, getRandomPrompt, fallbackPrompt]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;
    dispatch(actions.setTextPrompt(newText));
  };

  const placeholderText = useMemo(() => {
    if (isLoading) {
      return t('aiMusicGenerator:textToMusic:loadingPrompt');
    }
    return placeholderPrompt;
  }, [isLoading, placeholderPrompt, t]);

  return (
    <StyledContainer>
      <StyledFormulaContainer>
        {isMobile && (
          <>
            <PromptTypeToggle />
            <UserLoop />
          </>
        )}

        <StyledTextField
          required
          fullWidth
          multiline
          placeholder={placeholderText}
          value={text}
          onChange={handleChange}
          inputProps={{
            maxLength: MAX_LENGTH,
          }}
        />

        <StyledHintTextWrapper>
          <StyledInstrumentalLabel>
            {t('aiMusicGenerator:textToMusic:instrumentalOnly')}
          </StyledInstrumentalLabel>
          <StyledCharacterCount>
            {text.length}/{MAX_LENGTH}
          </StyledCharacterCount>
        </StyledHintTextWrapper>
        {hasUploadedAudio && <TextToMusicLoopField />}
        <StyledButtonContainer>
          <GenerateButton disabled={isLoading} onClick={submitFormula} />
        </StyledButtonContainer>
      </StyledFormulaContainer>
    </StyledContainer>
  );
};

export default TextToMusicFormulaPanel;
